// src/Lexcode/components/GestureControl.jsx
import React, { useRef, useEffect } from "react";
import * as handpose from "@tensorflow-models/handpose";
import "@tensorflow/tfjs";

export default function GestureControl({ onDrag }) {
  const videoRef  = useRef(null);
  const canvasRef = useRef(null);
  const modelRef  = useRef(null);
  const ctxRef    = useRef(null);
  const dimsRef   = useRef({ vw: 0, vh: 0, cw: 0, ch: 0 });
  const lastPos   = useRef({ x: 0, y: 0 });

  const FPS       = 30;
  const SMOOTHING = 0.2;

  useEffect(() => {
    let intervalId, stream;

    async function init() {
      // 1) 打开摄像头
      stream = await navigator.mediaDevices.getUserMedia({ video: true });
      const video = videoRef.current;
      video.srcObject = stream;
      await new Promise(r => (video.onloadedmetadata = r));
      video.play();

      // 2) 记录尺寸
      dimsRef.current.vw = video.videoWidth;
      dimsRef.current.vh = video.videoHeight;
      const container = document.querySelector(".lexcode-container");
      dimsRef.current.cw = container.clientWidth;
      dimsRef.current.ch = container.clientHeight;

      // 3) 初始化 Canvas
      const canvas = canvasRef.current;
      canvas.width  = dimsRef.current.cw;
      canvas.height = dimsRef.current.ch;
      const ctx = canvas.getContext("2d");
      ctx.lineWidth   = 2;
      ctx.strokeStyle = "lime";
      ctx.fillStyle   = "lime";
      ctxRef.current  = ctx;

      // 4) 加载 Handpose 模型
      modelRef.current = await handpose.load();

      // 5) 启动固定帧率循环
      intervalId = setInterval(renderFrame, 1000 / FPS);
    }

    // 选一只“五指伸直”的手
    function pickDragHand(preds) {
      return (
        preds.find(p => {
          const w = p.landmarks[0];
          return [4,8,12,16,20].every(i => {
            const dx = p.landmarks[i][0] - w[0];
            const dy = p.landmarks[i][1] - w[1];
            return Math.hypot(dx, dy) > 60;
          });
        }) || null
      );
    }

    async function renderFrame() {
      const { vw, vh, cw, ch } = dimsRef.current;
      const ctx   = ctxRef.current;
      const model = modelRef.current;
      const video = videoRef.current;
      if (!model || !video.videoWidth) return;

      // —— 镜像输入 ——  
      const preds = await model.estimateHands(video, { flipHorizontal: true });
      ctx.clearRect(0, 0, cw, ch);

      // —— 画手和骨骼 ——  
      const bones = [
        [0,1],[1,2],[2,3],[3,4],
        [0,5],[5,6],[6,7],[7,8],
        [0,9],[9,10],[10,11],[11,12],
        [0,13],[13,14],[14,15],[15,16],
        [0,17],[17,18],[18,19],[19,20]
      ];
      preds.forEach(p => {
        const lm = p.landmarks.map(([x,y]) => [(x/vw)*cw, (y/vh)*ch]);
        // 手掌填充
        ctx.save();
        ctx.globalAlpha = 0.2;
        ctx.beginPath();
        [0,5,9,13,17].forEach((i, idx) => {
          const [X,Y] = lm[i];
          idx===0?ctx.moveTo(X,Y):ctx.lineTo(X,Y);
        });
        ctx.closePath();
        ctx.fill();
        ctx.restore();
        // 指区填充
        ctx.save();
        ctx.globalAlpha = 0.1;
        [[0,1,2,3,4],[0,5,6,7,8],[0,9,10,11,12],[0,13,14,15,16],[0,17,18,19,20]]
          .forEach(seg => {
            ctx.beginPath();
            seg.forEach((i, idx) => {
              const [X,Y] = lm[i];
              idx===0?ctx.moveTo(X,Y):ctx.lineTo(X,Y);
            });
            ctx.closePath();
            ctx.fill();
          });
        ctx.restore();
        // 骨骼 & 关节点
        lm.forEach(([X,Y]) => {
          ctx.beginPath();
          ctx.arc(X,Y,5,0,2*Math.PI);
          ctx.fill();
        });
        bones.forEach(([i,j]) => {
          const [X1,Y1]=lm[i], [X2,Y2]=lm[j];
          ctx.beginPath();
          ctx.moveTo(X1,Y1);
          ctx.lineTo(X2,Y2);
          ctx.stroke();
        });
      });

      // —— 拖拽逻辑 ——  
      const hand = pickDragHand(preds);
      if (hand) {
        const [x8,y8] = hand.landmarks[8];
        // 1) 正常映射成画布坐标
        const px = (x8/vw)*cw;
        const py = (y8/vh)*ch;
        // 2) **再做一次左右翻转**，因为 DOM 定位不是镜像的：
        const rawX = cw - px - 25;
        const rawY = py - 25;
        // 3) 平滑
        const nx = lastPos.current.x + (rawX - lastPos.current.x)*SMOOTHING;
        const ny = lastPos.current.y + (rawY - lastPos.current.y)*SMOOTHING;
        lastPos.current = { x: nx, y: ny };
        onDrag({ x: nx, y: ny });
      }
    }

    init();
    return () => {
      clearInterval(intervalId);
      if (stream) stream.getTracks().forEach(t => t.stop());
    };
  }, [onDrag]);

  return (
    <>
      {/* 镜像显示摄像头（可调透明度或设 0 隐藏） */}
      <video
        ref={videoRef}
        style={{
          position:      "absolute",
          top:           0,
          left:          0,
          width:         "100%",
          height:        "100%",
          transform:     "scaleX(-1)",
          opacity:       0.3,
          pointerEvents: "none",
        }}
        muted
        playsInline
      />
      {/* 镜像画布 */}
      <canvas
        ref={canvasRef}
        style={{
          position:      "absolute",
          top:           0,
          left:          0,
          width:         "100%",
          height:        "100%",
          transform:     "scaleX(-1)",
          pointerEvents: "none",
        }}
      />
    </>
  );
}
